function _classPrivateFieldLooseBase(e, t) { if (!{}.hasOwnProperty.call(e, t)) throw new TypeError("attempted to use private field on non-instance"); return e; }
var id = 0;
function _classPrivateFieldLooseKey(e) { return "__private_" + id++ + "_" + e; }
import { Component } from '@theme/component';
import { debounce, onAnimationEnd, prefersReducedMotion } from '@theme/utilities';
import { sectionRenderer } from '@theme/section-renderer';
import { morph } from '@theme/morph';
import { RecentlyViewed } from '@theme/recently-viewed-products';
import { DialogCloseEvent, DialogOpenEvent, DialogComponent } from '@theme/dialog';

/**
 * A custom element that allows the user to search for resources available on the store.
 *
 * @typedef {object} Refs
 * @property {HTMLInputElement} searchInput - The search input element.
 * @property {HTMLElement} predictiveSearchResults - The predictive search results container.
 * @property {HTMLElement} resetButton - The reset button element.
 * @property {HTMLElement[]} [resultsItems] - The search results items elements.
 * @property {HTMLElement} [recentlyViewedWrapper] - The recently viewed products wrapper.
 * @property {HTMLElement[]} [recentlyViewedTitle] - The recently viewed title elements.
 * @property {HTMLElement[]} [recentlyViewedItems] - The recently viewed product items.
 * @extends {Component<Refs>}
 */
var _controller = _classPrivateFieldLooseKey("controller");
var _activeFetch = _classPrivateFieldLooseKey("activeFetch");
var _emptyStateLoaded = _classPrivateFieldLooseKey("emptyStateLoaded");
var _handleModalClick = _classPrivateFieldLooseKey("handleModalClick");
var _handleKeyboardShortcut = _classPrivateFieldLooseKey("handleKeyboardShortcut");
var _handleDialogClose = _classPrivateFieldLooseKey("handleDialogClose");
var _handleDialogOpen = _classPrivateFieldLooseKey("handleDialogOpen");
var _loadEmptyState = _classPrivateFieldLooseKey("loadEmptyState");
var _allResultsItems = _classPrivateFieldLooseKey("allResultsItems");
var _isKeyboardNavigation = _classPrivateFieldLooseKey("isKeyboardNavigation");
var _currentIndex = _classPrivateFieldLooseKey("currentIndex");
var _currentItem = _classPrivateFieldLooseKey("currentItem");
var _resetScrollPositions = _classPrivateFieldLooseKey("resetScrollPositions");
var _getSearchResults = _classPrivateFieldLooseKey("getSearchResults");
var _getRecentlyViewedProductsMarkup = _classPrivateFieldLooseKey("getRecentlyViewedProductsMarkup");
var _hideResetButton = _classPrivateFieldLooseKey("hideResetButton");
var _showResetButton = _classPrivateFieldLooseKey("showResetButton");
var _createAbortController = _classPrivateFieldLooseKey("createAbortController");
var _resetSearch = _classPrivateFieldLooseKey("resetSearch");
class PredictiveSearchComponent extends Component {
  constructor(...args) {
    super(...args);
    Object.defineProperty(this, _createAbortController, {
      value: _createAbortController2
    });
    Object.defineProperty(this, _showResetButton, {
      value: _showResetButton2
    });
    Object.defineProperty(this, _hideResetButton, {
      value: _hideResetButton2
    });
    /**
     * Fetch the markup for the recently viewed products.
     * @returns {Promise<string | null>} The markup for the recently viewed products.
     */
    Object.defineProperty(this, _getRecentlyViewedProductsMarkup, {
      value: _getRecentlyViewedProductsMarkup2
    });
    /**
     * Fetch search results using the section renderer and update the results container.
     * @param {string} searchTerm - The term to search for
     */
    Object.defineProperty(this, _getSearchResults, {
      value: _getSearchResults2
    });
    /**
     * Resets scroll positions for search results containers
     */
    Object.defineProperty(this, _resetScrollPositions, {
      value: _resetScrollPositions2
    });
    Object.defineProperty(this, _currentItem, {
      get: _get_currentItem,
      set: void 0
    });
    Object.defineProperty(this, _currentIndex, {
      get: _get_currentIndex,
      set: _set_currentIndex
    });
    Object.defineProperty(this, _allResultsItems, {
      get: _get_allResultsItems,
      set: void 0
    });
    Object.defineProperty(this, _loadEmptyState, {
      value: _loadEmptyState2
    });
    this.requiredRefs = ['searchInput', 'predictiveSearchResults', 'resetButton'];
    Object.defineProperty(this, _controller, {
      writable: true,
      value: new AbortController()
    });
    /**
     * @type {AbortController | null}
     */
    Object.defineProperty(this, _activeFetch, {
      writable: true,
      value: null
    });
    Object.defineProperty(this, _emptyStateLoaded, {
      writable: true,
      value: false
    });
    /**
     * Handles clicks within the predictive search modal to maintain focus on the input
     * @param {MouseEvent} event - The mouse event
     */
    Object.defineProperty(this, _handleModalClick, {
      writable: true,
      value: event => {
        const target = /** @type {HTMLElement} */event.target;
        const isInteractiveElement = target instanceof HTMLButtonElement || target instanceof HTMLAnchorElement || target instanceof HTMLInputElement || target.closest('button') || target.closest('a') || target.closest('input');
        if (!isInteractiveElement && this.refs.searchInput) {
          this.refs.searchInput.focus();
        }
      }
    });
    /**
     * Handles the CMD+K key combination.
     * @param {KeyboardEvent} event - The keyboard event.
     */
    Object.defineProperty(this, _handleKeyboardShortcut, {
      writable: true,
      value: event => {
        if (event.metaKey && event.key === 'k') {
          this.dialog?.toggleDialog();
        }
      }
    });
    /**
     * Handles the dialog close event.
     */
    Object.defineProperty(this, _handleDialogClose, {
      writable: true,
      value: () => {
        _classPrivateFieldLooseBase(this, _resetSearch)[_resetSearch]();
      }
    });
    Object.defineProperty(this, _handleDialogOpen, {
      writable: true,
      value: () => {
        if (!_classPrivateFieldLooseBase(this, _emptyStateLoaded)[_emptyStateLoaded] && RecentlyViewed.getProducts().length > 0) {
          _classPrivateFieldLooseBase(this, _loadEmptyState)[_loadEmptyState]();
        }
      }
    });
    /**
     * Track whether the last interaction was keyboard-based
     * @type {boolean}
     */
    Object.defineProperty(this, _isKeyboardNavigation, {
      writable: true,
      value: false
    });
    /**
     * Navigate through the predictive search results using arrow keys or close them with the Escape key.
     * @param {KeyboardEvent} event - The keyboard event.
     */
    this.onSearchKeyDown = event => {
      if (event.key === 'Escape') {
        _classPrivateFieldLooseBase(this, _resetSearch)[_resetSearch]();
        return;
      }
      if (!_classPrivateFieldLooseBase(this, _allResultsItems)[_allResultsItems]?.length || event.key === 'ArrowLeft' || event.key === 'ArrowRight') {
        return;
      }
      const currentIndex = _classPrivateFieldLooseBase(this, _currentIndex)[_currentIndex];
      const totalItems = _classPrivateFieldLooseBase(this, _allResultsItems)[_allResultsItems].length;
      switch (event.key) {
        case 'ArrowDown':
          _classPrivateFieldLooseBase(this, _isKeyboardNavigation)[_isKeyboardNavigation] = true;
          event.preventDefault();
          _classPrivateFieldLooseBase(this, _currentIndex)[_currentIndex] = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
          break;
        case 'Tab':
          if (event.shiftKey) {
            _classPrivateFieldLooseBase(this, _isKeyboardNavigation)[_isKeyboardNavigation] = true;
            event.preventDefault();
            _classPrivateFieldLooseBase(this, _currentIndex)[_currentIndex] = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
          } else {
            _classPrivateFieldLooseBase(this, _isKeyboardNavigation)[_isKeyboardNavigation] = true;
            event.preventDefault();
            _classPrivateFieldLooseBase(this, _currentIndex)[_currentIndex] = currentIndex < totalItems - 1 ? currentIndex + 1 : 0;
          }
          break;
        case 'ArrowUp':
          _classPrivateFieldLooseBase(this, _isKeyboardNavigation)[_isKeyboardNavigation] = true;
          event.preventDefault();
          _classPrivateFieldLooseBase(this, _currentIndex)[_currentIndex] = currentIndex > 0 ? currentIndex - 1 : totalItems - 1;
          break;
        case 'Enter':
          {
            const singleResultContainer = this.refs.predictiveSearchResults.querySelector('[data-single-result-url]');
            if (singleResultContainer instanceof HTMLElement && singleResultContainer.dataset.singleResultUrl) {
              event.preventDefault();
              window.location.href = singleResultContainer.dataset.singleResultUrl;
              return;
            }
            if (_classPrivateFieldLooseBase(this, _currentIndex)[_currentIndex] >= 0) {
              event.preventDefault();
              _classPrivateFieldLooseBase(this, _currentItem)[_currentItem]?.querySelector('a')?.click();
            } else {
              const searchUrl = new URL(Theme.routes.search_url, location.origin);
              searchUrl.searchParams.set('q', this.refs.searchInput.value);
              window.location.href = searchUrl.toString();
            }
            break;
          }
      }
    };
    /**
     * Reset the search state.
     * @param {boolean} [keepFocus=true] - Whether to keep focus on input after reset
     */
    this.resetSearch = debounce((keepFocus = true) => {
      if (keepFocus) {
        this.refs.searchInput.focus();
      }
      _classPrivateFieldLooseBase(this, _resetSearch)[_resetSearch]();
    }, 100);
    /**
     * Debounce the search handler to fetch and display search results based on the input value.
     * Reset the current selection index and close results if the search term is empty.
     */
    this.search = debounce(event => {
      // If the input is not a text input (like using the Escape key), don't search
      if (!event.inputType) return;
      const searchTerm = this.refs.searchInput.value.trim();
      _classPrivateFieldLooseBase(this, _currentIndex)[_currentIndex] = -1;
      if (!searchTerm.length) {
        _classPrivateFieldLooseBase(this, _resetSearch)[_resetSearch]();
        return;
      }
      _classPrivateFieldLooseBase(this, _showResetButton)[_showResetButton]();
      _classPrivateFieldLooseBase(this, _getSearchResults)[_getSearchResults](searchTerm);
    }, 200);
    Object.defineProperty(this, _resetSearch, {
      writable: true,
      value: async () => {
        const {
          predictiveSearchResults,
          searchInput
        } = this.refs;
        const emptySectionId = 'predictive-search-empty';
        _classPrivateFieldLooseBase(this, _currentIndex)[_currentIndex] = -1;
        searchInput.value = '';
        _classPrivateFieldLooseBase(this, _hideResetButton)[_hideResetButton]();
        const abortController = _classPrivateFieldLooseBase(this, _createAbortController)[_createAbortController]();
        const url = new URL(window.location.href);
        url.searchParams.delete('page');
        const emptySectionMarkup = await sectionRenderer.getSectionHTML(emptySectionId, false, url);
        const parsedEmptySectionMarkup = new DOMParser().parseFromString(emptySectionMarkup, 'text/html').querySelector('.predictive-search-empty-section');
        if (!parsedEmptySectionMarkup) throw new Error('No empty section markup found');

        /** This needs to be awaited and not .then so the DOM is already morphed
         * when #closeResults is called and therefore the height is animated */
        const viewedProducts = RecentlyViewed.getProducts();
        if (viewedProducts.length > 0) {
          const recentlyViewedMarkup = await _classPrivateFieldLooseBase(this, _getRecentlyViewedProductsMarkup)[_getRecentlyViewedProductsMarkup]();
          if (!recentlyViewedMarkup) return;
          const parsedRecentlyViewedMarkup = new DOMParser().parseFromString(recentlyViewedMarkup, 'text/html');
          const recentlyViewedProductsHtml = parsedRecentlyViewedMarkup.getElementById('predictive-search-products');
          if (!recentlyViewedProductsHtml) return;
          for (const child of recentlyViewedProductsHtml.children) {
            if (child instanceof HTMLElement) {
              child.setAttribute('ref', 'recentlyViewedWrapper');
            }
          }
          const collectionElement = parsedEmptySectionMarkup.querySelector('#predictive-search-products');
          if (!collectionElement) return;
          collectionElement.prepend(...recentlyViewedProductsHtml.children);
        }
        if (abortController.signal.aborted) return;
        morph(predictiveSearchResults, parsedEmptySectionMarkup);
        _classPrivateFieldLooseBase(this, _resetScrollPositions)[_resetScrollPositions]();
      }
    });
  }
  /**
   * Get the dialog component.
   * @returns {DialogComponent | null} The dialog component.
   */
  get dialog() {
    return this.closest('dialog-component');
  }
  connectedCallback() {
    super.connectedCallback();
    const {
      dialog
    } = this;
    const {
      signal
    } = _classPrivateFieldLooseBase(this, _controller)[_controller];
    if (this.refs.searchInput.value.length > 0) {
      _classPrivateFieldLooseBase(this, _showResetButton)[_showResetButton]();
    }
    if (dialog) {
      document.addEventListener('keydown', _classPrivateFieldLooseBase(this, _handleKeyboardShortcut)[_handleKeyboardShortcut], {
        signal
      });
      dialog.addEventListener(DialogCloseEvent.eventName, _classPrivateFieldLooseBase(this, _handleDialogClose)[_handleDialogClose], {
        signal
      });
      dialog.addEventListener(DialogOpenEvent.eventName, _classPrivateFieldLooseBase(this, _handleDialogOpen)[_handleDialogOpen], {
        signal,
        once: true
      });
      this.addEventListener('click', _classPrivateFieldLooseBase(this, _handleModalClick)[_handleModalClick], {
        signal
      });
    }
    if (RecentlyViewed.getProducts().length > 0) {
      requestIdleCallback(() => {
        _classPrivateFieldLooseBase(this, _loadEmptyState)[_loadEmptyState]();
      });
    }
  }
  disconnectedCallback() {
    super.disconnectedCallback();
    _classPrivateFieldLooseBase(this, _controller)[_controller].abort();
  }
  /**
   * Clears the recently viewed products.
   * @param {Event} event - The event.
   */
  clearRecentlyViewedProducts(event) {
    event.stopPropagation();
    RecentlyViewed.clearProducts();
    const {
      recentlyViewedItems,
      recentlyViewedTitle,
      recentlyViewedWrapper
    } = this.refs;
    const allRecentlyViewedElements = [...(recentlyViewedItems || []), ...(recentlyViewedTitle || [])];
    if (allRecentlyViewedElements.length === 0) {
      return;
    }
    if (recentlyViewedWrapper) {
      recentlyViewedWrapper.classList.add('removing');
      onAnimationEnd(recentlyViewedWrapper, () => {
        recentlyViewedWrapper.remove();
      });
    }
  }
}
function _loadEmptyState2() {
  if (_classPrivateFieldLooseBase(this, _emptyStateLoaded)[_emptyStateLoaded]) return;
  _classPrivateFieldLooseBase(this, _emptyStateLoaded)[_emptyStateLoaded] = true;
  this.resetSearch(false);
}
function _get_allResultsItems() {
  const containers = Array.from(this.querySelectorAll('.predictive-search-results__wrapper-queries, ' + '.predictive-search-results__wrapper-products, ' + '.predictive-search-results__list'));
  const allItems = containers.flatMap(container => {
    if (container.classList.contains('predictive-search-results__wrapper-products')) {
      return Array.from(container.querySelectorAll('.predictive-search-results__card'));
    }
    return Array.from(container.querySelectorAll('[ref="resultsItems[]"], .predictive-search-results__card'));
  }).filter(item => item instanceof HTMLElement);
  return /** @type {HTMLElement[]} */allItems;
}
function _get_currentIndex() {
  return _classPrivateFieldLooseBase(this, _allResultsItems)[_allResultsItems]?.findIndex(item => item.getAttribute('aria-selected') === 'true') ?? -1;
}
function _set_currentIndex(index) {
  if (!_classPrivateFieldLooseBase(this, _allResultsItems)[_allResultsItems]?.length) return;
  let activeItem = null;
  _classPrivateFieldLooseBase(this, _allResultsItems)[_allResultsItems].forEach(item => {
    item.classList.remove('keyboard-focus');
  });
  for (const [itemIndex, item] of _classPrivateFieldLooseBase(this, _allResultsItems)[_allResultsItems].entries()) {
    if (itemIndex === index) {
      item.setAttribute('aria-selected', 'true');
      if (_classPrivateFieldLooseBase(this, _isKeyboardNavigation)[_isKeyboardNavigation]) {
        item.classList.add('keyboard-focus');
      }
      activeItem = item;
    } else {
      item.removeAttribute('aria-selected');
    }
  }
  activeItem?.scrollIntoView({
    behavior: prefersReducedMotion() ? 'instant' : 'smooth',
    block: 'nearest'
  });
  this.refs.searchInput.focus();
}
function _get_currentItem() {
  return _classPrivateFieldLooseBase(this, _allResultsItems)[_allResultsItems]?.[_classPrivateFieldLooseBase(this, _currentIndex)[_currentIndex]];
}
function _resetScrollPositions2() {
  requestAnimationFrame(() => {
    this.refs.predictiveSearchResults.querySelector('.predictive-search-results__inner')?.scrollTo(0, 0);
    this.querySelector('.predictive-search-form__content')?.scrollTo(0, 0);
  });
}
async function _getSearchResults2(searchTerm) {
  if (!this.dataset.sectionId) return;
  const url = new URL(Theme.routes.predictive_search_url, location.origin);
  url.searchParams.set('q', searchTerm);
  url.searchParams.set('resources[limit_scope]', 'each');
  const {
    predictiveSearchResults
  } = this.refs;
  const abortController = _classPrivateFieldLooseBase(this, _createAbortController)[_createAbortController]();
  sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url).then(resultsMarkup => {
    if (!resultsMarkup) return;
    if (abortController.signal.aborted) return;
    morph(predictiveSearchResults, resultsMarkup);
    _classPrivateFieldLooseBase(this, _resetScrollPositions)[_resetScrollPositions]();
  }).catch(error => {
    if (abortController.signal.aborted) return;
    throw error;
  });
}
async function _getRecentlyViewedProductsMarkup2() {
  if (!this.dataset.sectionId) return null;
  const viewedProducts = RecentlyViewed.getProducts();
  if (viewedProducts.length === 0) return null;
  const url = new URL(Theme.routes.search_url, location.origin);
  url.searchParams.set('q', viewedProducts.map(/** @param {string} id */id => `id:${id}`).join(' OR '));
  url.searchParams.set('resources[type]', 'product');
  return sectionRenderer.getSectionHTML(this.dataset.sectionId, false, url);
}
function _hideResetButton2() {
  const {
    resetButton
  } = this.refs;
  resetButton.hidden = true;
}
function _showResetButton2() {
  const {
    resetButton
  } = this.refs;
  resetButton.hidden = false;
}
function _createAbortController2() {
  const abortController = new AbortController();
  if (_classPrivateFieldLooseBase(this, _activeFetch)[_activeFetch]) {
    _classPrivateFieldLooseBase(this, _activeFetch)[_activeFetch].abort();
  }
  _classPrivateFieldLooseBase(this, _activeFetch)[_activeFetch] = abortController;
  return abortController;
}
if (!customElements.get('predictive-search-component')) {
  customElements.define('predictive-search-component', PredictiveSearchComponent);
}
